import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  UploadCloud,
  Sparkles,
  TrendingDown,
  Eye,
  EyeOff,
  ShieldCheck,
  Sun,
  Moon,
} from "lucide-react";
import { motion } from "framer-motion";
import Button from "../other/Button";
import Input from "../other/Input";
import AuroraBackground from "../other/AuroraBackground";
import ConfirmDialog from "../other/ConfirmDialog";

const features = [
  {
    icon: UploadCloud,
    tint: "#4285F4",
    title: "Upload your cloud billing",
    body: "Drop in your cloud cost & usage report and CostIQ instantly parses every service, region, and line item.",
  },
  {
    icon: Sparkles,
    tint: "#34A853",
    title: "AI-mapped Google Cloud equivalents",
    body: "CostIQ maps each resource to its Google Cloud equivalent with live pricing — no spreadsheets required.",
  },
  {
    icon: TrendingDown,
    tint: "#FBBC05",
    title: "Migration-ready TCO & savings",
    body: "Get a downloadable Google Cloud TCO workbook with projected savings and a clear business case for your migration.",
  },
];

// Empty string = same-origin (LB routes /api/* to the backend) so cookies stay first-party.
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || "";

const AuthForm = ({ darkMode, setDarkMode }) => {
  const [authMode, setAuthMode] = useState("LOGIN"); // "LOGIN", "SIGNUP", "VERIFY_EMAIL", "FORGOT_PASSWORD", "RESET_PASSWORD"
  const isSignup = authMode === "SIGNUP";
  const [form, setForm] = useState({ email: "", password: "", name: "", otp: "", token: "", newPassword: "" });
  const [loading, setLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendMessage, setResendMessage] = useState("");
  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showRedirectConfirm, setShowRedirectConfirm] = useState(false); // <-- Add this state
  const googleBtnRef = useRef(null);
  const googleWrapRef = useRef(null);
  const [googleBtnWidth, setGoogleBtnWidth] = useState(360);

  // Keep Google's (invisible) button sized to the visual button so the whole
  // area stays clickable across breakpoints.
  useEffect(() => {
    const el = googleWrapRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const update = () =>
      setGoogleBtnWidth(Math.min(400, Math.round(el.offsetWidth)) || 360);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [isSignup]);

  useEffect(() => {
    // Check for error parameters in URL (from legacy Google OAuth redirects)
    const urlParams = new URLSearchParams(window.location.search);
    const errorParam = urlParams.get("error");
    const tokenParam = urlParams.get("token");
    const emailParam = urlParams.get("email");

    if (tokenParam && emailParam) {
      setAuthMode("RESET_PASSWORD");
      setForm(prev => ({ ...prev, token: tokenParam, email: emailParam }));
      // Clean URL
      window.history.replaceState({}, document.title, window.location.pathname);
    }

    if (errorParam) {
      const errorMessages = {
        missing_email: "Google authentication failed: email not provided",
        email_mismatch: "The Google account email doesn't match your signup email",
        user_already_exists: "User already exists. Please sign in instead",
        user_not_found: "Account not found. Please sign up first",
        database_error: "Server error occurred. Please try again",
        oauth_failed: "Google authentication failed. Please try again",
      };
      setError(errorMessages[errorParam] || "Authentication failed");
    }
  }, []);

  // Exchange the Google ID token for an application session on the backend.
  const handleCredentialResponse = useCallback(async (response) => {
    if (!response?.credential) {
      setError("Google authentication failed. Please try again.");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${BACKEND_URL}/api/auth/google/token`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ credential: response.credential }),
      });
      const data = await res.json();
      if (res.ok) {
        if (data.user) {
          localStorage.setItem("user", JSON.stringify(data.user));
        }
        // replace, not href: don't leave /login in browser history — the
        // back button should never be able to return to it once signed in.
        window.location.replace("/chat");
      } else {
        setError(
          data.detail || "Google authentication failed. Please try again."
        );
        setLoading(false);
      }
    } catch (err) {
      console.error("Google auth error:", err);
      setError(
        "Unable to connect to server. Please check if the backend is running."
      );
      setLoading(false);
    }
  }, []);

  // Initialize Google Identity Services and render the Sign-in button.
  useEffect(() => {
    if (!GOOGLE_CLIENT_ID) {
      console.warn(
        "REACT_APP_GOOGLE_CLIENT_ID is not set; Google sign-in disabled."
      );
      return;
    }
    let cancelled = false;

    const setup = () => {
      if (cancelled || !window.google?.accounts?.id) return false;
      window.google.accounts.id.initialize({
        client_id: GOOGLE_CLIENT_ID,
        callback: handleCredentialResponse,
        use_fedcm_for_prompt: true,
      });
      // Render Google's real button INVISIBLY beneath our custom glass button.
      // Our styled button sits on top (pointer-events: none) so clicks fall
      // through to Google's button, which handles the ID-token flow. This lets
      // us match the app's design while staying on the compliant GIS button.
      if (googleBtnRef.current && googleBtnRef.current.children.length === 0) {
        window.google.accounts.id.renderButton(googleBtnRef.current, {
          type: "standard",
          theme: "filled_black",
          size: "large",
          text: "continue_with",
          shape: "pill",
          logo_alignment: "left",
          width: googleBtnWidth,
        });
      }
      return true;
    };

    if (!setup()) {
      const timer = setInterval(() => {
        if (setup()) clearInterval(timer);
      }, 100);
      return () => {
        cancelled = true;
        clearInterval(timer);
      };
    }
    return () => {
      cancelled = true;
    };
  }, [handleCredentialResponse, isSignup, googleBtnWidth]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    setError(""); // Clear error when user starts typing
  };

  const isValidEmail = (email) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const isValidPassword = (pwd) => {
    // Minimum 8 characters, at least 1 letter and 1 number
    return /^(?=.*[A-Za-z])(?=.*\d).{8,}$/.test(pwd);
  };

  const validateForm = () => {
    if (!form.email.trim()) {
      setError(isSignup ? "Email is required" : "Email or Username is required");
      return false;
    }

    if (isSignup && !isValidEmail(form.email.trim())) {
      setError("Please enter a valid email address");
      return false;
    }

    if (!form.password.trim()) {
      setError("Password is required");
      return false;
    }

    if (isSignup && !isValidPassword(form.password)) {
      setError("Password must be at least 8 characters and include a letter and a number");
      return false;
    }

    if (isSignup && !form.name.trim()) {
      setError("Username is required for signup");
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (loading) return;

    setError("");

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    const endpoint = isSignup
      ? `${BACKEND_URL}/api/auth/pre-signup`
      : `${BACKEND_URL}/api/auth/login`;

    try {
      const params = new URLSearchParams();
      if (isSignup) {
        params.append("email", form.email.trim().toLowerCase());
        params.append("name", form.name.trim());
      } else {
        params.append("email", form.email.trim().toLowerCase());
      }
      params.append("password", form.password);

      const response = await fetch(endpoint, {
        method: "POST",
        body: params,
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
        credentials: "include",
      });

      const data = await response.json();

      if (response.ok) {
        if (isSignup && data.require_verification) {
          setAuthMode("VERIFY_EMAIL");
          setError("");
        } else {
          // Login successful
          if (data.user) {
            localStorage.setItem("user", JSON.stringify(data.user));
          }
          window.location.replace("/chat");
        }
      } else {
        // Handle error response. Backend sends a full sentence here (not a
        // stable code), so match on substring rather than exact equality.
        if (data.detail && data.detail.includes("Google Login")) {
          setError("GOOGLE_ACCOUNT_NO_PASSWORD");
        } else {
          setError(data.detail || "Authentication failed");
        }
      }
    } catch (err) {
      console.error("Auth error:", err);
      setError(
        "Unable to connect to server. Please check if the backend is running."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyEmail = async (e) => {
    e.preventDefault();
    if (loading) return;
    setLoading(true);
    setError("");

    try {
      const params = new URLSearchParams();
      params.append("email", form.email.trim().toLowerCase());
      params.append("otp", form.otp.trim());

      const response = await fetch(`${BACKEND_URL}/api/auth/verify-email`, {
        method: "POST",
        body: params,
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        credentials: "include",
      });

      const data = await response.json();
      if (response.ok) {
        if (data.user) {
          localStorage.setItem("user", JSON.stringify(data.user));
        }
        window.location.replace("/chat");
      } else {
        setError(data.detail || "Verification failed");
      }
    } catch (err) {
      setError("Unable to connect to server.");
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    if (resendLoading || !form.email) return;
    setResendLoading(true);
    setError("");
    setResendMessage("");

    try {
      const params = new URLSearchParams();
      params.append("email", form.email.trim().toLowerCase());

      const response = await fetch(`${BACKEND_URL}/api/auth/resend-otp`, {
        method: "POST",
        body: params,
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
      });

      const data = await response.json();
      if (response.ok) {
        setResendMessage("A new code has been sent to your email.");
      } else {
        setError(data.detail || "Failed to resend code");
      }
    } catch (err) {
      setError("Unable to connect to server.");
    } finally {
      setResendLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (loading) return;
    if (!form.email.trim()) {
      setError("Please enter your email.");
      return;
    }
    setLoading(true);
    setError("");

    try {
      const params = new URLSearchParams();
      params.append("email", form.email.trim().toLowerCase());

      const response = await fetch(`${BACKEND_URL}/api/auth/forgot-password`, {
        method: "POST",
        body: params,
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
      });

      const data = await response.json();
      if (response.ok) {
        setError("If an account exists, a reset link has been sent to your email."); // Show success message in error box (or add a success state)
        // Optionally switch back to login
      } else {
        setError(data.detail || "Request failed");
      }
    } catch (err) {
      setError("Unable to connect to server.");
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (loading) return;
    if (!isValidPassword(form.newPassword)) {
      setError("Password must be at least 8 characters and include a letter and a number");
      return;
    }
    setLoading(true);
    setError("");

    try {
      const params = new URLSearchParams();
      params.append("email", form.email.trim().toLowerCase());
      params.append("token", form.token);
      params.append("new_password", form.newPassword);

      const response = await fetch(`${BACKEND_URL}/api/auth/reset-password`, {
        method: "POST",
        body: params,
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
      });

      const data = await response.json();
      if (response.ok) {
        setAuthMode("LOGIN");
        setError("Password reset successful. You can now log in.");
      } else {
        setError(data.detail || "Reset failed");
      }
    } catch (err) {
      setError("Unable to connect to server.");
    } finally {
      setLoading(false);
    }
  };

  const toggleAuthMode = () => {
    setAuthMode(authMode === "LOGIN" ? "SIGNUP" : "LOGIN");
    setForm({ email: "", password: "", name: "", otp: "", token: "", newPassword: "" });
    setError("");
  };

  const handleCanexusRedirect = (e) => {
    e.preventDefault();
    setShowRedirectConfirm(true);
  };

  return (
    <div
      className={`relative flex flex-col w-full min-h-screen overflow-hidden transition-all duration-300 ${darkMode
        ? "text-white bg-[#0F172A]"
        : "text-slate-900 bg-gradient-to-br from-[#F8FBFF] via-[#FFFFFF] to-[#EEF6FF]"
        }`}
    >
      <>
        {darkMode && <AuroraBackground />}

        {!darkMode && (
          <>
            <div className="absolute -top-32 -left-32 w-[500px] h-[500px] rounded-full bg-cyan-200/40 blur-[140px]" />
            <div className="absolute top-1/3 left-1/4 w-[450px] h-[450px] rounded-full bg-sky-200/30 blur-[120px]" />
            <div className="absolute -bottom-32 right-0 w-[500px] h-[500px] rounded-full bg-blue-200/35 blur-[150px]" />
          </>
        )}
      </>

      {/* ---------------------------------------------------------------- */}
      {/*  Full-width announcement bar (Refined & Integrated)              */}
      {/* ---------------------------------------------------------------- */}
      <div
        className={`relative z-50 w-full overflow-hidden border-b transition-colors duration-300 ${darkMode
          ? "border-white/5 bg-[#0F172A]/40"
          : "border-slate-200/50 bg-white/40"
          }`}
        style={{ backdropFilter: "blur(12px)", WebkitBackdropFilter: "blur(12px)" }}
      >
        {/* Mask image creates a smooth fade effect on the extreme left and right edges */}
        <div
          className="flex overflow-hidden py-2.5"
          style={{
            WebkitMaskImage:
              "linear-gradient(to right, transparent 0%, black 5%, black 95%, transparent 100%)",
            maskImage:
              "linear-gradient(to right, transparent 0%, black 5%, black 95%, transparent 100%)",
          }}
        >
          <motion.div
            className="flex items-center whitespace-nowrap w-max"
            animate={{ x: ["0%", "-50%"] }}
            transition={{ repeat: Infinity, ease: "linear", duration: 80 }}
          >
            {/* Array mapped to duplicate items for a seamless marquee loop */}
            {[...Array(10)].map((_, i) => (
              <div key={i} className="flex items-center justify-center px-8">
                {/* Left badge */}
                <span
                  className="flex-shrink-0 flex items-center gap-1.5 rounded-full px-2.5 py-0.5 mr-3 text-[10px] font-bold uppercase tracking-widest"
                  style={{
                    background: darkMode
                      ? "rgba(255,153,0,0.12)"
                      : "rgba(255,153,0,0.1)",
                    border: "1px solid rgba(255,153,0,0.3)",
                    color: "#FF9900",
                  }}
                >
                  <span className="relative flex-shrink-0 w-1.5 h-1.5">
                    <span
                      className="absolute inset-0 rounded-full animate-ping opacity-75"
                      style={{ backgroundColor: "#34A853" }}
                    />
                    <span
                      className="relative block w-1.5 h-1.5 rounded-full"
                      style={{ backgroundColor: "#34A853" }}
                    />
                  </span>
                  Live
                </span>

                {/* Main caption */}
                <p
                  className={`text-[13px] leading-snug text-center ${darkMode ? "text-slate-300" : "text-slate-600"
                    }`}
                >
                  CostIQ delivers{" "}
                  <span
                    className={`font-semibold ${darkMode ? "text-white" : "text-slate-900"
                      }`}
                  >
                    AWS &rarr; Google Cloud
                  </span>{" "}
                  TCO assessments.{" "}
                  <span
                    className={darkMode ? "text-slate-400" : "text-slate-500"}
                  >
                    <svg
                      width="13"
                      height="13"
                      viewBox="0 0 16 16"
                      fill="none"
                      style={{
                        display: "inline",
                        verticalAlign: "middle",
                        marginRight: "3px",
                        opacity: 0.65,
                      }}
                    >
                      <circle cx="8" cy="8" r="7.5" stroke="currentColor" />
                      <path
                        d="M8 4.5v4l2.5 2.5"
                        stroke="currentColor"
                        strokeWidth="1.4"
                        strokeLinecap="round"
                      />
                    </svg>
                    <span
                      className="font-medium"
                      style={{ color: darkMode ? "#60a5fa" : "#2563EB" }}
                    >
                      Azure support
                    </span>{" "}
                    is coming soon.
                  </span>
                </p>
              </div>
            ))}
          </motion.div>
        </div>
      </div>

      {/* Dark mode toggle - Original Position */}
      <div className="absolute top-14 right-8 z-50">
        <button
          onClick={() => setDarkMode(!darkMode)}
          className={`w-11 h-11 rounded-xl border flex items-center justify-center transition-all duration-300 ${darkMode
            ? "bg-white/10 border-white/10 text-white hover:bg-white/20"
            : "bg-white border-slate-300 text-slate-700 shadow-md hover:bg-slate-100"
            }`}
        >
          {darkMode ? (
            <Sun className="w-5 h-5" />
          ) : (
            <Moon className="w-5 h-5" />
          )}
        </button>
      </div>

      <div className="relative z-10 flex flex-col md:flex-row flex-1">
        {/* ---------------------------------------------------------------- */}
        {/*  Left — brand / value panel                                      */}
        {/* ---------------------------------------------------------------- */}
        <div
          className={`relative z-10 hidden md:flex md:w-[52%] lg:w-[55%] flex-col justify-between p-10 lg:p-14 ${darkMode
            ? ""
            : "bg-gradient-to-br from-sky-50/60 via-transparent to-blue-50/40"
            }`}
        >
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          >
            <a href="https://canexus.ai/" onClick={handleCanexusRedirect} className="inline-block">
              <img
                src="/images/Cloud-Ambassadors-logo.png"
                alt="Cloud Ambassadors"
                className="h-9 lg:h-11 w-auto"
              />
            </a>

            {/* Brand lockup */}
            <div className="mt-10 mb-7">
              <div className="flex flex-wrap items-baseline gap-x-3 gap-y-2">
                <span
                  className={`text-5xl lg:text-6xl font-extrabold tracking-tight ${darkMode ? "text-white" : "text-slate-900"
                    }`}
                >
                  Cost<span className="text-gradient-google">IQ</span>
                </span>
                <span
                  className={`px-3 py-1 rounded-full text-[11px] font-semibold uppercase tracking-wider ${darkMode
                    ? "glass text-accent-200"
                    : "bg-sky-100 text-sky-700 border border-sky-200"
                    }`}
                >
                  by Cloud Ambassadors
                </span>
              </div>
              <p
                className={`mt-3 text-sm font-semibold tracking-[0.18em] ${darkMode ? "text-accent-300" : "text-slate-600"
                  } uppercase`}
              >
                AI-Powered Total Cost Optimization Platform
              </p>
            </div>

            <h1
              className={`text-3xl lg:text-[2.6rem] font-bold leading-[1.15] mb-9 max-w-xl ${darkMode ? "text-white" : "text-slate-900"
                }`}
            >
              <span className="text-gradient-accent">Optimize</span> every dollar.{" "}
              <span className="text-gradient-google">Maximize</span> every workload.
            </h1>

            <div className="space-y-3.5">
              {features.map((f, i) => {
                const Icon = f.icon;
                return (
                  <motion.div
                    key={f.title}
                    initial={{ opacity: 0, x: -14 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.2 + i * 0.12, duration: 0.5 }}
                    className={`group flex gap-4 rounded-2xl p-3.5 transition-colors ${darkMode ? "hover:bg-white/[0.03]" : "hover:bg-sky-50"
                      }`}
                  >
                    <div
                      className="w-13 h-13 shrink-0 rounded-2xl flex items-center justify-center border transition-transform group-hover:scale-105"
                      style={{
                        background: darkMode ? `${f.tint}1f` : `${f.tint}15`,
                        borderColor: darkMode ? `${f.tint}4d` : `${f.tint}30`,
                        width: "3.25rem",
                        height: "3.25rem",
                      }}
                    >
                      <Icon className="w-6 h-6" style={{ color: f.tint }} />
                    </div>
                    <div>
                      <h3
                        className={`text-base font-semibold ${darkMode ? "text-white" : "text-slate-900"
                          }`}
                      >
                        {f.title}
                      </h3>
                      <p
                        className={`text-sm ${darkMode
                          ? "text-slate-300 leading-relaxed max-w-md mt-0.5"
                          : "text-slate-600 leading-relaxed max-w-md mt-0.5"
                          }`}
                      >
                        {f.body}
                      </p>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.55, duration: 0.6 }}
            className={`relative mt-12 rounded-3xl p-7 max-w-xl ${darkMode
              ? "glass-sheen glass shadow-e2"
              : "bg-white border border-slate-200 shadow-xl"
              }`}
          >
            <p
              className={`italic text-lg leading-relaxed ${darkMode ? "text-slate-200" : "text-slate-700"
                }`}
            >
              "Give leadership a defensible, data-driven cost case for migrating
              to Google Cloud — generated in minutes, not weeks."
            </p>
            <p
              className={`mt-4 font-medium text-sm ${darkMode ? "text-accent-300" : "text-sky-700"
                }`}
            >
              CostIQ · Cloud Ambassadors
            </p>
          </motion.div>
        </div>

        {/* ---------------------------------------------------------------- */}
        {/*  Right — auth card                                               */}
        {/* ---------------------------------------------------------------- */}
        <div className="relative z-10 w-full md:w-[48%] lg:w-[45%] flex items-center justify-center p-6 sm:p-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            className={`relative w-full max-w-md rounded-4xl p-8 sm:p-10 ${darkMode
              ? "glass-sheen glass-strong shadow-e3"
              : "bg-white border border-slate-200 shadow-2xl"
              }`}
          >
            {/* Mobile logo */}
            <a
              href="https://canexus.ai/"
              onClick={handleCanexusRedirect}
              className="inline-block mb-6 md:hidden"
            >
              <img
                src="/images/Cloud-Ambassadors-logo.png"
                alt="Cloud Ambassadors"
                className="h-8 w-auto"
              />
            </a>

            {/* Header */}
            <div className="mb-7">
              <h2
                className={`text-2xl sm:text-3xl font-bold tracking-tight ${darkMode ? "text-white" : "text-slate-900"
                  }`}
              >
                {isSignup ? (
                  "Create your account"
                ) : (
                  <>
                    Welcome to Cost
                    <span className="text-gradient-google">IQ</span>
                  </>
                )}
              </h2>
              <p
                className={`text-sm mt-2 ${darkMode ? "text-slate-400" : "text-slate-600"
                  }`}
              >
                {isSignup
                  ? "Sign up to start building your GCP migration TCO."
                  : "Continue with Google or enter your details."}
              </p>
            </div>

            {/* Google Sign-In — custom glass button over the (invisible) GIS button */}
            {(authMode === "LOGIN" || authMode === "SIGNUP") && (
              <div
                ref={googleWrapRef}
                className="relative w-full h-[48px] select-none"
              >
                {/* Google's real button: invisible (opacity 0) but still captures
                    the click; our glass button on top provides the entire visual. */}
                <div
                  ref={googleBtnRef}
                  className="absolute inset-0 flex items-center justify-center overflow-hidden opacity-0"
                  aria-hidden="false"
                />
                {/* Our styled button: on top, visual only (clicks pass through) */}
                <div
                  className={`pointer-events-none absolute inset-0 flex items-center justify-center gap-3 rounded-2xl shadow-glow-sm ${darkMode
                    ? "glass glass-sheen border border-white/10 text-white"
                    : "bg-white border border-slate-300 text-slate-700"
                    }`}
                >
                  <svg className="w-5 h-5" viewBox="0 0 24 24">
                    <path
                      fill="#4285F4"
                      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                    />
                    <path
                      fill="#34A853"
                      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                    />
                    <path
                      fill="#FBBC05"
                      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                    />
                    <path
                      fill="#EA4335"
                      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                    />
                  </svg>
                  <span className="text-base font-medium">
                    Continue with Google
                  </span>
                </div>
              </div>
            )}

            {/* OR Divider */}
            {(authMode === "LOGIN" || authMode === "SIGNUP") && (
              <div className="flex items-center my-6">
                <div className="flex-1 h-px bg-white/10" />
                <span className="px-4 text-slate-500 text-xs font-medium uppercase tracking-wider">
                  or
                </span>
                <div className="flex-1 h-px bg-white/10" />
              </div>
            )}

            {/* Form */}
            <div className="space-y-4">
              {error && error === "GOOGLE_ACCOUNT_NO_PASSWORD" ? (
                <motion.div
                  initial={{ opacity: 0, y: -6 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`flex flex-col gap-2 px-4 py-3 rounded-xl text-sm border ${darkMode
                    ? "bg-amber-500/10 border-amber-500/40 text-amber-200"
                    : "bg-amber-50 border-amber-400 text-amber-700"
                    }`}
                >
                  <p>This account uses Google Login. To login with a password, you must create one first.</p>
                  <button onClick={() => { setAuthMode("FORGOT_PASSWORD"); setError(""); }} className="font-semibold underline text-left">Click here to create a password</button>
                </motion.div>
              ) : error ? (
                <motion.div
                  initial={{ opacity: 0, y: -6 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`flex items-start gap-2 px-4 py-2.5 rounded-xl text-sm border ${darkMode
                    ? "bg-rose-500/12 border-rose-500/40 text-rose-200"
                    : "bg-rose-50 border-rose-400 text-rose-700"
                    }`}
                >
                  {error}
                </motion.div>
              ) : null}


              {authMode === "VERIFY_EMAIL" && (
                <>
                  <Input
                    label="Verification Code (OTP)"
                    darkMode={darkMode}
                    name="otp"
                    type="text"
                    placeholder="Enter the 6-digit code"
                    value={form.otp}
                    onChange={handleChange}
                    required
                  />
                  <Button
                    onClick={handleVerifyEmail}
                    variant="primary" size="lg" loading={loading} loadingText="Verifying..." className="w-full rounded-2xl mb-2"
                  >
                    Verify Email
                  </Button>

                  {resendMessage && (
                    <p className="text-center text-xs font-medium text-green-500 mb-2">{resendMessage}</p>
                  )}

                  <div className="flex flex-col items-center gap-2 mt-2">
                    <button
                      type="button"
                      onClick={handleResendCode}
                      disabled={resendLoading}
                      className={`text-sm transition-colors disabled:opacity-50 ${darkMode
                        ? "text-accent-300 hover:text-accent-200"
                        : "text-sky-600 hover:text-sky-700"
                        }`}
                    >
                      {resendLoading ? "Sending..." : "Resend Code"}
                    </button>

                    <button
                      type="button"
                      onClick={() => {
                        setAuthMode("LOGIN");
                        setError("");
                        setResendMessage("");
                      }}
                      className={`text-sm transition-colors ${darkMode
                        ? "text-slate-400 hover:text-slate-300"
                        : "text-slate-500 hover:text-slate-700"
                        }`}
                    >
                      Back to login
                    </button>
                  </div>
                </>
              )}

              {authMode === "FORGOT_PASSWORD" && (
                <>
                  <Input
                    label="Email"
                    darkMode={darkMode}
                    name="email"
                    type="email"
                    placeholder="example@gmail.com"
                    value={form.email}
                    onChange={handleChange}
                    required
                  />
                  <Button
                    onClick={handleForgotPassword}
                    variant="primary" size="lg" loading={loading} loadingText="Sending..." className="w-full rounded-2xl"
                  >
                    Send Reset Link
                  </Button>
                  <div className="text-center mt-2">
                    <button onClick={() => setAuthMode("LOGIN")} type="button" className="text-sm text-sky-600">Back to Login</button>
                  </div>
                </>
              )}

              {authMode === "RESET_PASSWORD" && (
                <>
                  <Input
                    label="New Password"
                    darkMode={darkMode}
                    name="newPassword"
                    type={showPassword ? "text" : "password"}
                    placeholder="Enter new password"
                    value={form.newPassword}
                    onChange={handleChange}
                    required
                    minLength={6}
                  />
                  <Button
                    onClick={handleResetPassword}
                    variant="primary" size="lg" loading={loading} loadingText="Resetting..." className="w-full rounded-2xl"
                  >
                    Reset Password
                  </Button>
                </>
              )}

              {(authMode === "LOGIN" || authMode === "SIGNUP") && (
                <>
                  {isSignup && (
                    <Input
                      label="Username"
                      darkMode={darkMode}
                      name="name"
                      type="text"
                      placeholder="Enter your username"
                      value={form.name}
                      onChange={handleChange}
                      required={isSignup}
                    />
                  )}

                  <Input
                    label={isSignup ? "Email" : "Email or Username"}
                    darkMode={darkMode}
                    name="email"
                    type={isSignup ? "email" : "text"}
                    autoComplete={isSignup ? "email" : "username"}
                    placeholder={isSignup ? "example@gmail.com" : "Enter email or username"}
                    value={form.email}
                    onChange={handleChange}
                    required
                  />

                  <div>
                    <label
                      className={`block text-sm font-medium mb-2 ${darkMode ? "text-slate-300" : "text-slate-700"
                        }`}
                    >
                      Password
                    </label>
                    <div className="relative">
                      <Input
                        name="password"
                        darkMode={darkMode}
                        type={showPassword ? "text" : "password"}
                        autoComplete="current-password"
                        placeholder="••••••••"
                        value={form.password}
                        onChange={handleChange}
                        required
                        minLength={6}
                        className="pr-12"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className={`absolute right-3 top-1/2 -translate-y-1/2 p-1.5 rounded-lg transition-colors ${darkMode
                          ? "text-slate-400 hover:text-white hover:bg-white/[0.06]"
                          : "text-slate-500 hover:text-slate-900 hover:bg-slate-100"
                          }`}
                        aria-label={showPassword ? "Hide password" : "Show password"}
                      >
                        {showPassword ? (
                          <EyeOff className="w-5 h-5" />
                        ) : (
                          <Eye className="w-5 h-5" />
                        )}
                      </button>
                    </div>
                    {!isSignup && (
                      <div className="mt-2 text-right">
                        <button
                          type="button"
                          onClick={() => setAuthMode("FORGOT_PASSWORD")}
                          className={`text-sm transition-colors ${darkMode
                            ? "text-accent-300 hover:text-accent-200"
                            : "text-sky-600 hover:text-sky-700"
                            }`}
                        >
                          Forgot your password?
                        </button>
                      </div>
                    )}
                  </div>

                  <Button
                    onClick={handleSubmit}
                    variant="primary"
                    size="lg"
                    loading={loading}
                    loadingText="Processing..."
                    disabled={loading}
                    className="w-full rounded-2xl"
                  >
                    {isSignup ? "Create Account" : "Login"}
                  </Button>
                </>
              )}


              {/* Toggle Auth Mode */}
              {(authMode === "LOGIN" || authMode === "SIGNUP") && (
                <div className="text-center pt-1">
                  <span className="text-slate-400 text-sm">
                    {isSignup
                      ? "Already have an account? "
                      : "Don't have an account? "}
                  </span>
                  <button
                    type="button"
                    onClick={toggleAuthMode}
                    disabled={loading}
                    className={`font-semibold text-sm transition-colors ${darkMode
                      ? "text-accent-300 hover:text-accent-200"
                      : "text-sky-600 hover:text-sky-700"
                      }`}
                  >
                    {isSignup ? "Sign In" : "Sign up"}
                  </button>
                </div>
              )}

              {isSignup && (
                <p className="text-xs text-slate-500 text-center leading-relaxed px-4">
                  After clicking "Create Account", we will send a 6-digit code
                  to your email to verify your address.
                </p>
              )}

              <div className="flex items-center justify-center gap-2 pt-2 text-slate-500 text-xs">
                <ShieldCheck className="w-3.5 h-3.5" />
                <span>Secured with Google OAuth 2.0</span>
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* ---------------------------------------------------------------- */}
      {/*  Footer                                                          */}
      {/* ---------------------------------------------------------------- */}
      <footer
        className={`relative z-10 border-t ${darkMode ? "border-white/10" : "border-slate-200"
          }`}
      >
        <div className="max-w-6xl mx-auto px-6 lg:px-10 py-8 flex flex-col sm:flex-row items-start sm:items-center gap-8 sm:gap-10">
          {/* Brand + description */}
          <div className="flex items-center gap-4">
            <img
              src="/images/Cloud-Ambassadors-logo.png"
              alt="Cloud Ambassadors"
              className="h-8 w-auto shrink-0"
            />
            <p
              className={`text-sm leading-relaxed max-w-xs ${darkMode ? "text-slate-400" : "text-slate-600"
                }`}
            >
              AI-powered Total Cost Optimization for cloud migrations —
              upload your cloud invoice and get an instant Google Cloud TCO
              estimate.
            </p>
          </div>

          <div
            className={`hidden sm:block w-px self-stretch ${darkMode ? "bg-white/10" : "bg-slate-200"
              }`}
          />

          {/* CA Nexus callout */}
          <div>
            <p
              className={`text-xs font-bold uppercase tracking-wider ${darkMode ? "text-amber-400" : "text-amber-600"
                }`}
            >
              Cloud Ambassadors
            </p>
            <p
              className={`mt-1 text-sm ${darkMode ? "text-slate-400" : "text-slate-600"
                }`}
            >
              Visit the{" "}
              <a
                href="https://canexus.ai/"
                className={`font-medium ${darkMode
                  ? "text-amber-400 hover:text-amber-300"
                  : "text-amber-600 hover:text-amber-700"
                  }`}
              >
                CA Nexus Innovation Hub
              </a>{" "}
              to view our other Cloud Solutions.
            </p>
          </div>
        </div>

        <div
          className={`border-t ${darkMode ? "border-white/10" : "border-slate-200"
            }`}
        >
          <div className="max-w-6xl mx-auto px-6 lg:px-10 py-5 flex items-center justify-center gap-1.5 text-xs">
            <span className="text-slate-500">
              © {new Date().getFullYear()} Cloud Ambassadors.
            </span>
            <span className="text-slate-500">All Rights Reserved.</span>
          </div>
        </div>
      </footer>
      {/* ---------------------------------------------------------------- */}
      {/*  Redirect Confirmation Modal                                     */}
      {/* ---------------------------------------------------------------- */}
      <ConfirmDialog
        open={showRedirectConfirm}
        tone="default"
        title="Continue to CANexus?"
        message={
          <>
            You’re about to leave CostIQ and visit the CA Nexus Innovation Hub.
            <br /><br />
          </>
        }
        confirmLabel="Continue to CANexus"
        cancelLabel="Cancel"
        onConfirm={() => window.location.href = "https://canexus.ai/"}
        onCancel={() => setShowRedirectConfirm(false)}
        darkMode={darkMode}
      />
    </div>
  );
};

export default AuthForm;